import { Component, OnInit, OnDestroy, EventEmitter } from '@angular/core';
import { Observable, Subscription } from 'rxjs';
import { map } from 'rxjs/operators';
import { MatDialog } from '@angular/material/dialog';
import { ActionButtonEvent } from 'src/app/layout/action-button-toolbar/action-button-toolbar.component';
import { ColumnSetting } from 'src/app/layout/table-standard-layout/table-standard-layout.component';
import { CouponService } from '../coupon.service';
import { CouponDialogComponent } from '../coupon-dialog/coupon-dialog.component';
import { CouponStatsComponent } from '../coupon-stats/coupon-stats.component';
import { DialogDelete } from 'src/app/dialog-delete/dialog-delete.component';
import { NotificationService } from 'src/app/shared/notification.service';

@Component({
  selector: 'app-coupons-list',
  templateUrl: './coupons-list.component.html',
  styleUrls: ['./coupons-list.component.css']
})
export class CouponsListComponent implements OnInit, OnDestroy {

  coupons$: Observable<any[]>;

  rowActionButtons: { icon: string; tooltip: string; eventEmitter: EventEmitter<ActionButtonEvent>; }[];
  topbarActionButton: { icon: string; tooltip: string; eventEmitter: EventEmitter<ActionButtonEvent>; }[];

  editCoupon: EventEmitter<ActionButtonEvent>;
  onEditCouponSubscription: Subscription;

  toggleActive: EventEmitter<ActionButtonEvent>;
  onToggleActiveSubscription: Subscription;

  deleteCoupon: EventEmitter<ActionButtonEvent>;
  onDeleteCouponSubscription: Subscription;

  viewStats: EventEmitter<ActionButtonEvent>;
  onViewStatsSubscription: Subscription;

  addCoupon: EventEmitter<ActionButtonEvent>;
  onAddCouponSubscription: Subscription;

  columnSettings: ColumnSetting[] = [
    { columnId: 'code', name: 'Code' },
    { columnId: 'discountTypeLabel', name: 'Type', hideOnMobile: true },
    { columnId: 'discountValueLabel', name: 'Value', hideOnMobile: true },
    { columnId: 'remainingBalanceLabel', name: 'Balance', hideOnMobile: true },
    { columnId: 'validUntil', name: 'Expires', hideOnMobile: true },
    { columnId: 'isActive', name: 'Active' }
  ];

  constructor(
    private couponService: CouponService,
    private dialog: MatDialog,
    private notifierService: NotificationService
  ) {

    this.coupons$ = this.couponService.getCoupons$().pipe(
      map(coupons => coupons.map(coupon => ({
        ...coupon,
        discountTypeLabel: coupon.discountType === 'fixed' ? 'Gavekort' : 'Legacy percentage',
        discountValueLabel: coupon.discountType === 'percentage'
          ? `${coupon.discountValue}%`
          : `NOK ${coupon.discountValue}`,
        remainingBalanceLabel: coupon.discountType === 'fixed'
          ? `NOK ${coupon.remainingBalance ?? coupon.discountValue}`
          : '—',
        validUntil: coupon.validUntil?.toDate
          ? coupon.validUntil.toDate().toLocaleDateString('nb-NO')
          : coupon.validUntil,
        validFrom: coupon.validFrom?.toDate
          ? coupon.validFrom.toDate().toLocaleDateString('nb-NO')
          : coupon.validFrom
      })))
    );

    this.editCoupon = new EventEmitter<ActionButtonEvent>();
    this.toggleActive = new EventEmitter<ActionButtonEvent>();
    this.deleteCoupon = new EventEmitter<ActionButtonEvent>();
    this.viewStats = new EventEmitter<ActionButtonEvent>();
    this.addCoupon = new EventEmitter<ActionButtonEvent>();

    this.rowActionButtons = [
      { icon: 'edit', tooltip: 'Edit Coupon', eventEmitter: this.editCoupon },
      { icon: 'toggle_on', tooltip: 'Toggle Active', eventEmitter: this.toggleActive },
      { icon: 'bar_chart', tooltip: 'View Stats', eventEmitter: this.viewStats },
      { icon: 'delete', tooltip: 'Delete Coupon', eventEmitter: this.deleteCoupon }
    ];

    this.topbarActionButton = [
      { icon: 'add', tooltip: 'Add Coupon', eventEmitter: this.addCoupon }
    ];

    this.onAddCouponSubscription = this.addCoupon.subscribe(() => {
      this.dialog.open(CouponDialogComponent, {
        width: '550px',
        disableClose: true,
        data: null
      });
    });

    this.onEditCouponSubscription = this.editCoupon.subscribe(event => {
      const data = event.eventData;
      if (data) {
        this.dialog.open(CouponDialogComponent, {
          width: '550px',
          disableClose: true,
          data: data
        });
      }
    });

    this.onToggleActiveSubscription = this.toggleActive.subscribe(async event => {
      const data = event.eventData;
      if (data) {
        try {
          await this.couponService.toggleActive(data['code'], !data['isActive']);
          this.notifierService.showNotification('success', 'Coupon status updated');
        } catch (err) {
          this.notifierService.showNotification('error', 'Failed to update coupon status');
        }
      }
    });

    this.onViewStatsSubscription = this.viewStats.subscribe(event => {
      const data = event.eventData;
      if (data) {
        this.dialog.open(CouponStatsComponent, {
          width: '550px',
          disableClose: false,
          data: data
        });
      }
    });

    this.onDeleteCouponSubscription = this.deleteCoupon.subscribe(event => {
      const data = event.eventData;
      if (data) {
        const dialogRef = this.dialog.open(DialogDelete, {
          width: '550px',
          disableClose: true,
          data: {
            formData: {
              title: 'DELETE',
              message: `Are you sure you want to delete coupon "${data['code']}"?`,
              buttons: ['Cancel', 'Yes']
            }
          }
        });
        dialogRef.afterClosed().subscribe(async result => {
          if (result) {
            try {
              await this.couponService.deleteCoupon(data['code']);
              this.notifierService.showNotification('success', 'Coupon deleted');
            } catch (err) {
              this.notifierService.showNotification('error', 'Failed to delete coupon');
            }
          }
        });
      }
    });
  }

  ngOnInit() {}

  ngOnDestroy() {
    this.onAddCouponSubscription.unsubscribe();
    this.onEditCouponSubscription.unsubscribe();
    this.onToggleActiveSubscription.unsubscribe();
    this.onViewStatsSubscription.unsubscribe();
    this.onDeleteCouponSubscription.unsubscribe();
  }
}
